import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import UserActionsMenu from '../UserActionsMenu';
import useLongPress from '../../hooks/useLongPress';
import useMediaQuery from '../../utils/useMediaQuery';
import MentionText from '../MentionText';
import PlanCard from './plan/PlanCard';
import HighlightedText from './plan/HighlightedText';
import { VoiceNoteCard } from './VoicePlayer';
import VoiceTranscript from './VoiceTranscript';
import { fillFor } from '../wall/wallTokens';
import ReactionTray from './ReactionTray';
import {
  T, RADIUS, BODY_FONT, EMOJI_FONT, formatTime, previewText, firstNameOf, bubbleRadius, portalRoomLabel, portalStatus, viewOnceState, viewOnceLabel, viewsLeft, bodySize, bubbleMax, callRecordView,
} from './messagesModel';
import ViewLimitIcon from './ViewLimitIcon';
import VideoMessageCard from './VideoMessage';
import AlbumGrid, { AlbumStatus } from './AlbumBubble';
import CallRecordCard from './CallRecordCard';
import { flyBadge, collapseBubble } from './messagesMotion';

const EDIT_WINDOW_MS = 20 * 60 * 1000;

/**
 * One message in the thread (handoff: "Thread pane" + "Reactions").
 *
 * Bubbles have no border: theirs are white, mine Nintendo blue, both black type, 20px
 * corners with a 6px tail on the last bubble of a run. The quote, the Portal card and the
 * reaction badges all belong to the bubble; the tray is a child too, so a tap inside it
 * never reaches the thread's tap-away handler.
 */
export default function MessageBubble({
  item,
  isGroup = false,
  highlighted = false,
  trayOpen = false,
  trayMore = false,
  onOpenTray,
  onCloseTray,
  onTrayMore,
  onReply,
  onCopy,
  onEdit,
  onUnsend,
  onReact,
  onRetry,
  onDiscard,
  onOpenImage,
  onOpenVideo,
  /** Albums: open the viewer at an index; retry one tile; SEND WITHOUT IT. */
  onOpenAlbum,
  onRetryItem,
  onDropFailed,
  /** Calls: CALL BACK on a record (voice or video, from the record's own kind). */
  onCallBack,
  peerName = null,
  onJumpTo,
  /** View once: the viewer's id (the state is a question of who is asking) and the open handler. */
  viewerUserId = null,
  onOpenViewOnce,
  /** "Seen 1m ago" — on the one bubble that carries it (ThreadPane decides which). */
  seen = null,
  /** Voice notes: what this viewer has heard of it, whether it is up in the dock, and a tap handler. */
  voiceRanges,
  voiceActive = false,
  voicePlaying = false,
  voiceAt = 0,
  onPlayVoice,
  onSeekVoice,
  voiceNoteCount = 0,
  onOpenVoiceNotes,
  voicePeaks = null,
  /** Plan detection: the phrases the plan was pulled from this message, and the sheet opener. */
  highlights = null,
  onOpenPlan = null,
  highlightDelay = 180,
}) {
  const { msg, mine, firstOfRun, lastOfRun } = item;
  const outbox = msg._outbox;
  const outboxState = outbox?.state;
  const inFlight = outboxState === 'sending' || outboxState === 'queued';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(msg.body || '');
  const [busy, setBusy] = useState(false);
  // Optimistic reaction state so a tap feels instant; reconciled from the server's
  // returned message (which carries the authoritative `mine` flags).
  const [reactions, setReactions] = useState(msg.reactions || []);
  const [peerActionsOpen, setPeerActionsOpen] = useState(false);
  const hoverCapable = useMediaQuery('(hover: hover) and (pointer: fine)');
  const phone = useMediaQuery('(max-width: 767px)');
  const fontSize = bodySize(phone);
  const bubbleRef = useRef(null);
  const flyFrom = useRef(null);
  // Desktop only: a photo has no bubble to click, so a small chip appears on hover instead.
  const [photoHover, setPhotoHover] = useState(false);

  useEffect(() => { setReactions(msg.reactions || []); }, [msg.reactions]);
  // The transcript card pops under a voice note while it is up in the dock; × hides it until
  // the note is tapped again.
  // The transcript is asked for, never automatic: the TRANSCRIPT button on the voice card
  // opens it inside the same box, and the box takes the full width of the thread while it
  // is open so the words are read across the conversation, not down a 260px column.
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  const firstName = firstNameOf(msg.senderDisplayName);
  const showSender = !mine && !msg.system;
  const vo = viewOnceState(msg, viewerUserId);   // null for an ordinary message
  const openable = vo === 'sealed' || vo === 'replay';
  const editable = mine && msg.kind === 'text' && !vo && !outbox && (Date.now() - new Date(msg.createdAt).getTime() < EDIT_WINDOW_MS);
  const hasBadges = reactions.length > 0;
  const isMedia = msg.kind === 'gif' || msg.kind === 'link';

  // Desktop: a click opens. Touch: the long-press rule stays, and a tap only closes.
  const longPress = useLongPress(() => { if (!outbox && !editing) onOpenTray?.(msg.id); }, { delay: 420 });
  const onBubbleClick = (e) => {
    e.stopPropagation();
    if (outbox || editing) return;
    if (trayOpen) { onCloseTray?.(); return; }
    // A view-once message with a look left opens on a tap, on every kind of pointer.
    if (vo === 'sealed' || vo === 'replay') { onOpenViewOnce?.(msg); return; }
    if (hoverCapable) onOpenTray?.(msg.id);
  };

  // Toggle a reaction: fold it into local state, then reconcile with the server. The
  // badge flies in from the emoji that was tapped once React has drawn it.
  const react = async (emoji, fromRect = null) => {
    flyFrom.current = fromRect ? { emoji, rect: fromRect } : null;
    onCloseTray?.();
    setReactions((prev) => {
      const ex = prev.find((r) => r.emoji === emoji);
      if (ex && ex.mine) {
        const c = ex.count - 1;
        return c <= 0 ? prev.filter((r) => r.emoji !== emoji) : prev.map((r) => (r.emoji === emoji ? { ...r, count: c, mine: false } : r));
      }
      if (ex) return prev.map((r) => (r.emoji === emoji ? { ...r, count: r.count + 1, mine: true } : r));
      return [...prev, { emoji, count: 1, mine: true }];
    });
    const r = await onReact?.(msg.id, emoji);
    if (r?.success && r.data?.reactions) setReactions(r.data.reactions);
  };
  useLayoutEffect(() => {
    const f = flyFrom.current;
    if (!f) return;
    flyFrom.current = null;
    const badge = bubbleRef.current?.querySelector(`[data-chip="${CSS.escape(f.emoji)}"]`);
    if (badge) flyBadge({ badge, fromRect: f.rect });
  }, [reactions]);

  const saveEdit = async () => {
    const body = draft.trim();
    if (!body || body === msg.body) { setEditing(false); return; }
    setBusy(true);
    const r = await onEdit?.(msg.id, body);
    setBusy(false);
    if (r?.success) setEditing(false);
  };
  const unsend = async () => {
    onCloseTray?.();
    await collapseBubble(bubbleRef.current);
    setBusy(true);
    await onUnsend?.(msg.id);
    setBusy(false);
  };
  const copy = () => {
    const text = msg.body || previewText(msg);
    try { navigator.clipboard?.writeText(text).catch(() => {}); } catch { /* clipboard refused */ }
    onCloseTray?.();
    onCopy?.();
  };
  const reply = () => {
    onCloseTray?.();
    onReply?.({ id: msg.id, name: mine ? 'YOURSELF' : firstName, body: previewText(msg), senderDisplayName: msg.senderDisplayName });
  };
  const actions = [
    { label: 'REPLY', act: reply },
    ...(!vo && (msg.kind === 'text' || msg.kind === 'link') ? [{ label: 'COPY TEXT', act: copy }] : []),
    ...(editable ? [{ label: 'EDIT', act: () => { onCloseTray?.(); setDraft(msg.body || ''); setEditing(true); } }] : []),
    ...(mine ? [{ label: 'UNSEND', act: unsend, danger: true }] : []),
  ];

  // ── The published card (plan detection, board 1e): no bubble, the card speaks for the sender. ──
  if (msg.kind === 'plan') {
    return (
      <div data-mid={msg.id} className="flex" style={{ margin: '4px 0 8px', justifyContent: mine ? 'flex-end' : 'flex-start' }}>
        <PlanCard msg={msg} mine={mine} />
      </div>
    );
  }

  // ── System line ──
  if (msg.system) {
    return (
      <div data-mid={msg.id} className="text-center font-mono font-bold" style={{ margin: '10px 0', fontSize: 9, letterSpacing: '.1em', color: T.grey600 }}>
        {msg.body}
      </div>
    );
  }

  const timeStyle = { marginTop: 4, fontSize: 9, letterSpacing: '.1em', color: mine ? T.mineTime : T.grey500, textAlign: mine ? 'right' : 'left' };
  const timeLine = <div className="font-mono" style={timeStyle}>{formatTime(msg.createdAt)}{msg.editedAt ? ' · EDITED' : ''}</div>;

  const quote = msg.quote && (
    <div
      role="button"
      tabIndex={0}
      onClick={(e) => { e.stopPropagation(); onJumpTo?.(msg.quote.id); }}
      onKeyDown={(e) => { if (e.key === 'Enter') onJumpTo?.(msg.quote.id); }}
      style={{ margin: '0 -6px 8px', padding: '6px 10px', borderRadius: RADIUS.quote, background: 'rgba(0,0,0,.07)', cursor: 'pointer', maxWidth: 260 }}
    >
      <div className="font-mono font-bold" style={{ fontSize: 9, letterSpacing: '.1em', opacity: 0.75 }}>
        {(msg.quote.senderDisplayName || 'THEM').toUpperCase()}
      </div>
      <div
        style={{ marginTop: 2, fontSize: 12, lineHeight: 1.3, fontFamily: BODY_FONT, opacity: 0.85, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}
      >
        {msg.quote.bodySnippet}
      </div>
    </div>
  );

  const badges = hasBadges && (
    <div style={{ position: 'absolute', bottom: -18, ...(mine ? { right: 12 } : { left: 12 }), display: 'flex', gap: 2 }}>
      {reactions.map((r) => (
        <button
          key={r.emoji}
          type="button"
          data-chip={r.emoji}
          onClick={(e) => { e.stopPropagation(); react(r.emoji); }}
          aria-label={`${r.emoji} ${r.count}`}
          className="font-mono font-bold"
          style={{
            height: 28, minWidth: 28, padding: '0 6px', borderRadius: RADIUS.pill, border: '2px solid transparent',
            background: fillFor(r.mine ? 'on' : 'open'), color: r.mine ? T.white : T.ink,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, fontSize: 10, cursor: 'pointer',
          }}
        >
          <span style={{ fontSize: 14, lineHeight: '16px', display: 'block', fontFamily: EMOJI_FONT }}>{r.emoji}</span>
          {r.count > 1 && <span>{r.count}</span>}
        </button>
      ))}
    </div>
  );

  const portalCard = msg.portal && <PortalReplyCard portal={msg.portal} />;

  const bubbleStyle = {
    position: 'relative',
    borderRadius: bubbleRadius({ mine, lastOfRun }),
    background: mine ? T.mine : T.white,
    color: T.ink,
    padding: isMedia ? 4 : '10px 14px',
    marginBottom: hasBadges ? 14 : 0,
    // The voice box takes the thread's width while its transcript is out.
    ...(transcriptOpen ? { width: '100%' } : {}),
    cursor: outbox ? 'default' : 'pointer',
    opacity: inFlight ? 0.6 : 1,
    transition: 'opacity 200ms',
  };

  return (
    <div
      id={msg.id ? `msg-${msg.id}` : undefined}
      data-mid={msg.id}
      className={`flex items-end gap-2 ${mine ? 'justify-end' : 'justify-start'}`}
      style={{ marginBottom: lastOfRun ? 8 : 3, ...(highlighted ? { boxShadow: `0 0 0 3px ${T.beam}80`, borderRadius: 20, transition: 'box-shadow 400ms' } : {}) }}
    >
      {showSender && (
        // Press and hold the sender's avatar for block/report. The space is kept on every
        // bubble of the run so they line up; only the last one shows the face.
        <div className="relative flex-shrink-0" style={{ visibility: lastOfRun ? 'visible' : 'hidden', marginBottom: (hasBadges ? 14 : 0) + (msg.kind === 'album' ? 18 : 0) }}>
          <SenderAvatar msg={msg} firstName={firstName} onLongPress={() => setPeerActionsOpen(true)} />
          {msg.senderId && lastOfRun && (
            <UserActionsMenu
              open={peerActionsOpen}
              onClose={() => setPeerActionsOpen(false)}
              userId={msg.senderId}
              displayName={msg.senderDisplayName}
              messageId={msg.id}
              anchor="left"
              // Leaving the thread is the honest result: the conversation is over.
              onBlocked={() => { setPeerActionsOpen(false); window.location.href = '/messages'; }}
            />
          )}
        </div>
      )}

      <div className={`relative flex flex-col ${mine ? 'items-end' : 'items-start'}`} style={transcriptOpen ? { maxWidth: '100%', flex: '1 1 auto', minWidth: 0 } : { maxWidth: bubbleMax(phone) }}>
        {trayOpen && !editing && !outbox && (
          <ReactionTray
            mine={mine}
            reactions={reactions}
            more={trayMore}
            actions={actions}
            badgesHang={hasBadges}
            onPick={react}
            onMore={() => onTrayMore?.(true)}
            onLess={() => onTrayMore?.(false)}
            bubbleEl={bubbleRef.current}
          />
        )}

        {showSender && firstOfRun && (isGroup || firstName) && (
          <div className="font-mono font-bold" style={{ fontSize: 10, letterSpacing: '.1em', color: T.grey500, marginBottom: 2, padding: '0 2px' }}>
            {firstName}
          </div>
        )}

        {portalCard}

        {vo ? (
          /* View once: a sealed capsule in place of the content, whatever the kind. The ring
             means there is still a look to be had; opened / expired / sent are flat. */
          <div
            ref={bubbleRef}
            data-bubble
            data-testid="view-once-bubble"
            data-state={vo}
            role={openable ? 'button' : undefined}
            tabIndex={openable ? 0 : undefined}
            onClick={onBubbleClick}
            onKeyDown={openable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onOpenViewOnce?.(msg); } } : undefined}
            {...(hoverCapable ? {} : longPress)}
            style={{
              ...bubbleStyle,
              padding: '10px 14px',
              ...(openable ? { border: '2px solid transparent', background: fillFor('open'), color: T.ink } : {}),
              ...(vo === 'expired' ? { opacity: 0.55 } : {}),
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {/* The count in a portal ring: looks left for the reader, the limit for the sender, a tick once it's done. */}
              <ViewLimitIcon
                n={mine ? (msg.viewOnce?.viewLimit || 1) : Math.max(1, viewsLeft(msg, viewerUserId))}
                check={vo === 'opened'}
                muted={vo === 'expired' || vo === 'opened'}
                size={24}
              />
              <span>
                <span className="block font-mono font-bold" style={{ fontSize: 10, letterSpacing: '.12em' }}>{viewOnceLabel(msg, vo)}</span>
                {openable && <span className="block font-mono" style={{ fontSize: 9, letterSpacing: '.1em', marginTop: 2, opacity: 0.7 }}>{vo === 'replay' ? 'TAP TO REPLAY' : 'TAP TO VIEW'}</span>}
              </span>
            </div>
            {timeLine}
            {badges}
          </div>
        ) : !editing && msg.kind === 'album' ? (
          /* An album: the grid is its own frame. Tiles open the viewer; hold / hover chip for the tray. */
          <>
            <div
              ref={bubbleRef}
              data-bubble
              className="relative"
              {...(hoverCapable ? { onMouseEnter: () => setPhotoHover(true), onMouseLeave: () => setPhotoHover(false) } : longPress)}
              style={{ borderRadius: RADIUS.bubble, marginBottom: hasBadges ? 14 : 0 }}
            >
              <AlbumGrid
                msg={msg}
                items={outbox?.items || null}
                inFlight={inFlight}
                onOpen={(index) => { if (trayOpen) { onCloseTray?.(); return; } onOpenAlbum?.(msg, index); }}
                onRetryItem={(clientId) => onRetryItem?.(msg.id, clientId)}
              />
              {hoverCapable && !outbox && !trayOpen && (
                <button
                  type="button"
                  aria-label="Message options"
                  onClick={(e) => { e.stopPropagation(); onOpenTray?.(msg.id); }}
                  className="flex items-center justify-center"
                  style={{
                    position: 'absolute', top: 8, ...(mine ? { left: 8 } : { right: 8 }), width: 32, height: 32, borderRadius: '50%', border: 0,
                    background: 'rgba(255,255,255,.92)', color: T.ink, opacity: photoHover ? 1 : 0, transition: 'opacity 160ms', cursor: 'pointer',
                  }}
                >
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9.5" />
                    <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
                    <path d="M9 9.5v.01M15 9.5v.01" />
                  </svg>
                </button>
              )}
              {badges}
            </div>
            {timeLine}
          </>
        ) : !editing && msg.kind === 'call' ? (
          /* A call record: inside a bubble (mine blue, theirs white), CALL BACK when there is someone to call back. */
          <div
            ref={bubbleRef}
            data-bubble
            onClick={onBubbleClick}
            {...(hoverCapable ? {} : longPress)}
            style={{ ...bubbleStyle, padding: '10px 12px 8px 10px', minWidth: 200 }}
          >
            <CallRecordCard view={callRecordView(msg, viewerUserId, { peerName })} video={!!msg.call?.video} mine={mine} onCallBack={onCallBack ? (e) => { e.stopPropagation(); onCallBack(msg, e); } : null} />
            {timeLine}
            {badges}
          </div>
        ) : !editing && msg.kind === 'video' ? (
          /* A clip is its own frame too. The tray on hold / hover chip, like a photo. */
          <>
            <div
              ref={bubbleRef}
              data-bubble
              className="relative"
              {...(hoverCapable ? { onMouseEnter: () => setPhotoHover(true), onMouseLeave: () => setPhotoHover(false) } : longPress)}
              style={{ borderRadius: RADIUS.bubble, marginBottom: hasBadges ? 14 : 0 }}
            >
              <VideoMessageCard msg={msg} inFlight={inFlight} progress={outbox?.progress} onOpen={(m) => { if (trayOpen) { onCloseTray?.(); return; } onOpenVideo?.(m); }} />
              {hoverCapable && !outbox && !trayOpen && (
                <button
                  type="button"
                  aria-label="Message options"
                  onClick={(e) => { e.stopPropagation(); onOpenTray?.(msg.id); }}
                  className="flex items-center justify-center"
                  style={{
                    position: 'absolute', top: 8, ...(mine ? { left: 8 } : { right: 8 }), width: 32, height: 32, borderRadius: '50%', border: 0,
                    background: 'rgba(255,255,255,.92)', color: T.ink, opacity: photoHover ? 1 : 0, transition: 'opacity 160ms', cursor: 'pointer',
                  }}
                >
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9.5" />
                    <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
                    <path d="M9 9.5v.01M15 9.5v.01" />
                  </svg>
                </button>
              )}
              {badges}
            </div>
            {timeLine}
          </>
        ) : !editing && msg.kind === 'image' ? (
          /* A photo is its own frame — no bubble, rounded corners, the time under it. It still
             takes the tray: hold it on a phone, or use the chip that appears on hover. */
          <>
            <div
              ref={bubbleRef}
              data-bubble
              className="relative"
              {...(hoverCapable ? { onMouseEnter: () => setPhotoHover(true), onMouseLeave: () => setPhotoHover(false) } : longPress)}
              style={{ borderRadius: RADIUS.bubble, marginBottom: hasBadges ? 14 : 0 }}
            >
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  if (trayOpen) { onCloseTray?.(); return; }
                  if (msg.imageUrl) onOpenImage?.(msg);
                }}
                disabled={!msg.imageUrl}
                className="block"
                style={{ borderRadius: RADIUS.bubble, overflow: 'hidden', opacity: inFlight ? 0.6 : 1 }}
                aria-label={msg.imageFilename ? `Open ${msg.imageFilename}` : 'Open image'}
              >
                {msg.imageUrl
                  ? <img src={msg.imageUrl} alt={msg.imageFilename || ''} className="block max-h-[320px] max-w-full object-cover" draggable={false} />
                  : <div className="flex h-[180px] w-[240px] items-center justify-center font-mono text-[10px] font-bold tracking-widest" style={{ background: T.activeBadge }}>IMAGE</div>}
              </button>
              {hoverCapable && !outbox && !trayOpen && (
                <button
                  type="button"
                  aria-label="Message options"
                  onClick={(e) => { e.stopPropagation(); onOpenTray?.(msg.id); }}
                  className="flex items-center justify-center"
                  style={{
                    position: 'absolute', top: 8, ...(mine ? { left: 8 } : { right: 8 }), width: 32, height: 32, borderRadius: '50%', border: 0,
                    background: 'rgba(255,255,255,.92)', color: T.ink, opacity: photoHover ? 1 : 0, transition: 'opacity 160ms', cursor: 'pointer',
                  }}
                >
                  <svg width={18} height={18} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="9.5" />
                    <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
                    <path d="M9 9.5v.01M15 9.5v.01" />
                  </svg>
                </button>
              )}
              {badges}
            </div>
            {timeLine}
          </>
        ) : editing ? (
          <div style={{ background: T.white, borderRadius: RADIUS.bubble, padding: 10, width: 280, maxWidth: '100%' }}>
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={2}
              autoFocus
              className="w-full resize-none border-0 p-0 text-sm text-black outline-none focus:ring-0"
              style={{ fontFamily: BODY_FONT, background: 'transparent' }}
            />
            <div className="mt-2 flex justify-end gap-2">
              <PillButton onClick={() => { setEditing(false); setDraft(msg.body || ''); }}>CANCEL</PillButton>
              <PillButton primary onClick={saveEdit} disabled={busy}>SAVE</PillButton>
            </div>
          </div>
        ) : (
          <div
            ref={bubbleRef}
            data-bubble
            onClick={onBubbleClick}
            {...(hoverCapable ? {} : longPress)}
            style={bubbleStyle}
          >
            {msg.kind === 'text' && (
              <>
                {quote}
                <div style={{ fontSize, lineHeight: 1.35, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: BODY_FONT }}>
                  {highlights?.length ? <HighlightedText text={msg.body} quotes={highlights} onOpen={onOpenPlan} delayMs={highlightDelay} /> : <MentionText text={msg.body} />}
                </div>
              </>
            )}
            {msg.kind === 'audio' && (
              <div className="px-0.5 py-0.5" style={transcriptOpen ? { width: '100%' } : undefined}>
                <div className="flex items-end" style={{ gap: 8 }}>
                  <VoiceNoteCard
                    msg={msg}
                    ranges={voiceRanges || msg.audioListenedRanges || []}
                    active={voiceActive}
                    playing={voicePlaying}
                    at={voiceAt}
                    mine={mine}
                    noteCount={voiceNoteCount}
                    peaks={voicePeaks}
                    onPlay={onPlayVoice}
                    onOpenNotes={onOpenVoiceNotes}
                  />
                  <span style={{ flex: 1 }} />
                  <button
                    type="button"
                    data-voice-transcript-toggle
                    aria-expanded={transcriptOpen}
                    aria-label={transcriptOpen ? 'Hide transcript' : 'Read transcript'}
                    onClick={(e) => { e.stopPropagation(); setTranscriptOpen((o) => !o); }}
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 26, height: 26, borderRadius: '50%', padding: 0, cursor: 'pointer',
                      border: `2px solid ${mine ? T.ink : T.ink}`,
                      background: transcriptOpen ? T.ink : 'transparent',
                      color: transcriptOpen ? T.white : T.ink,
                      transition: 'background-color 200ms, color 200ms',
                    }}
                  >
                    {/* The words, and a chevron once they are out. */}
                    {transcriptOpen
                      ? <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="square" aria-hidden><path d="M6 15l6-6 6 6" /></svg>
                      : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" aria-hidden><path d="M4 7h16M4 12h16M4 17h10" /></svg>}
                  </button>
                </div>
                {transcriptOpen && (
                  /* Mine is a blue box; the words get their own white panel inside it. */
                  <div style={mine ? { marginTop: 8, background: T.white, borderRadius: 14, padding: '0 10px' } : undefined}>
                    <VoiceTranscript
                      msg={msg}
                      at={voiceActive ? voiceAt : -1}
                      onSeek={voiceActive ? onSeekVoice : null}
                      ephemeral={!!outbox}
                      inline
                    />
                  </div>
                )}
              </div>
            )}
            {msg.kind === 'gif' && (
              <div>
                {msg.gifUrl
                  ? <img src={msg.gifUrl} alt={msg.gifLabel || 'GIF'} className="block max-w-[280px] max-h-[280px]" style={{ borderRadius: 16 }} />
                  : <div className="w-[240px] h-[180px] flex items-center justify-center font-mono text-[11px] font-bold tracking-widest" style={{ background: T.activeBadge, borderRadius: 16 }}>{msg.gifLabel}</div>}
                <div className="px-2 py-1 font-mono text-[9px] tracking-widest flex justify-between" style={{ color: T.grey500 }}>
                  <span>GIF</span><span>via GIPHY</span>
                </div>
              </div>
            )}
            {msg.kind === 'link' && (
              <div>
                {quote && <div className="px-2 pt-2">{quote}</div>}
                {msg.body && (
                  <div className="px-3 pt-2" style={{ fontSize, lineHeight: 1.35, whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: BODY_FONT }}>
                    <MentionText text={msg.body} />
                  </div>
                )}
                <a href={msg.linkUrl} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()} className="block mt-2" style={{ color: 'inherit', textDecoration: 'none' }}>
                  {msg.linkImageUrl && (
                    <img src={msg.linkImageUrl} alt="" className="block w-[280px] max-w-full h-[140px] object-cover" style={{ borderRadius: 16 }} />
                  )}
                  <div className="px-3 py-2">
                    {msg.linkSiteName && <div className="font-mono text-[9px] font-bold tracking-widest opacity-70">{msg.linkSiteName.toUpperCase()}</div>}
                    <div className="text-[13px] font-bold leading-tight mt-0.5" style={{ fontFamily: BODY_FONT }}>{msg.linkTitle || msg.linkUrl}</div>
                    {msg.linkDescription && (
                      <div className="text-[12px] leading-snug opacity-75 line-clamp-2 mt-1" style={{ fontFamily: BODY_FONT }}>{msg.linkDescription}</div>
                    )}
                  </div>
                </a>
              </div>
            )}
            <div style={isMedia ? { padding: '0 8px 4px' } : undefined}>{timeLine}</div>
            {badges}
          </div>
        )}

        {outboxState && (outbox.items
          ? <AlbumStatus items={outbox.items} onRetry={() => onRetry?.(msg.id)} onDropFailed={() => onDropFailed?.(msg.id)} />
          : <OutboxStatus state={outboxState} error={outbox.error} onRetry={() => onRetry?.(msg.id)} onDiscard={() => onDiscard?.(msg.id)} />
        )}
        {seen && (
          <div data-testid="seen-receipt" className="font-mono" style={{ marginTop: hasBadges ? 18 : 4, fontSize: 10, letterSpacing: '.06em', color: T.grey500, textAlign: 'right', padding: '0 2px' }}>
            {seen}
          </div>
        )}
      </div>
    </div>
  );
}

function SenderAvatar({ msg, firstName, onLongPress }) {
  const press = useLongPress(onLongPress);
  return (
    <div
      {...press}
      role="button"
      tabIndex={0}
      aria-label={`Actions for ${firstName || 'sender'}`}
      className="overflow-hidden cursor-pointer select-none"
      style={{ width: 28, height: 28, borderRadius: RADIUS.small, background: T.activeBadge, WebkitTouchCallout: 'none' }}
    >
      {msg.senderAvatarUrl
        ? <img src={msg.senderAvatarUrl} alt="" draggable={false} className="w-full h-full object-cover pointer-events-none" />
        : <div className="w-full h-full flex items-center justify-center font-mono text-[11px] font-bold text-black">{(firstName || '?').charAt(0).toUpperCase()}</div>}
    </div>
  );
}

/** "REPLIED TO YOUR PORTAL" — the card above a bubble sent from the story viewer. */
export function PortalReplyCard({ portal, mine = false }) {
  const status = portalStatus(portal);
  return (
    <div
      style={{
        display: 'flex', gap: 10, border: '2px solid transparent', borderRadius: RADIUS.bubble, background: fillFor('open'),
        color: T.ink, padding: 8, marginBottom: 6, width: 280, maxWidth: '100%', boxSizing: 'border-box',
      }}
    >
      <span style={{ position: 'relative', width: 54, height: 96, flexShrink: 0, background: T.ink, overflow: 'hidden', display: 'block', borderRadius: RADIUS.thumb }}>
        {portal.thumbnailUrl
          ? <img src={portal.thumbnailUrl} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }} />
          : (
            <>
              <span style={{ position: 'absolute', left: 0, right: 0, top: 30, height: 4, background: T.beam }} />
              <img src="/portal-mark-192.png" alt="" style={{ position: 'absolute', right: -6, bottom: -6, width: 36, height: 36, opacity: 0.5 }} />
            </>
          )}
      </span>
      <span style={{ minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span className="font-mono font-bold" style={{ fontSize: 9, letterSpacing: '.1em', color: T.grey500 }}>
          {mine ? 'YOU REPLIED TO THEIR PORTAL' : 'REPLIED TO YOUR PORTAL'}
        </span>
        {portal.text && <span style={{ fontSize: 13, lineHeight: 1.3, fontFamily: BODY_FONT }}>{portal.text}</span>}
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span
            className="font-mono font-bold"
            style={{ borderRadius: RADIUS.pill, background: status.live ? T.live : T.dayPill, color: status.live ? T.white : T.grey600, fontSize: 8, letterSpacing: '.1em', padding: '2px 5px' }}
          >
            {status.label}
          </span>
          <span className="font-mono font-bold" style={{ fontSize: 9, letterSpacing: '.1em', color: T.grey500 }}>{portalRoomLabel(portal)}</span>
        </span>
      </span>
    </div>
  );
}

export function PillButton({ primary = false, danger = false, children, ...rest }) {
  return (
    <button
      type="button"
      className="font-mono font-bold disabled:opacity-40"
      style={{
        borderRadius: RADIUS.pill, border: `2px solid ${danger ? T.danger : T.ink}`,
        background: primary ? T.ink : 'transparent', color: primary ? T.white : danger ? T.danger : T.ink,
        fontSize: 10, letterSpacing: '.1em', padding: '5px 12px', cursor: 'pointer',
      }}
      {...rest}
    >
      {children}
    </button>
  );
}

/** The line under a bubble that hasn't landed yet: quiet while working, loud only on a failure. */
function OutboxStatus({ state, error, onRetry, onDiscard }) {
  if (state === 'sending' || state === 'queued') {
    return (
      <div className="mt-0.5 flex items-center justify-end gap-1.5 font-mono text-[9px] font-bold uppercase tracking-widest" style={{ color: T.grey500 }}>
        <span aria-hidden className="inline-block h-2.5 w-2.5 flex-shrink-0 animate-spin rounded-full border-2 border-gray-400 border-t-transparent" />
        <span>{state === 'sending' ? 'Sending…' : 'Offline — will send when you reconnect'}</span>
      </div>
    );
  }
  return (
    <div className="mt-0.5 flex items-center justify-end gap-2 font-mono text-[9px] font-bold uppercase tracking-widest" style={{ color: T.danger }}>
      <span className="truncate">{error || 'Not sent'}</span>
      <PillButton danger onClick={onRetry}>RETRY</PillButton>
      <PillButton danger onClick={onDiscard} aria-label="Discard message">×</PillButton>
    </div>
  );
}
